import "./Details.css";

import React, {memo, useEffect, useState} from "react";

import {Attributes, DocType, Phrases} from "features/validationRules/types";
import DocumentPhrasesView from "./DocumentPhrasesView";
import {SelectOptionsPanel} from "shared/components/inputFields";
import DocumentAttributesView from "./DocumentAttributesView";
import TestCustomPhraseComponent from "./TestCustomPhrase";

type ResType = "Phrases"|"Attributes";

const ResultsViewComponent = ({cat_id, phrases, attributes}: {cat_id: number, phrases: Phrases|undefined, attributes: Attributes|undefined }) => {
    const [selectedDocTypeIdx, setSelectedDocTypeIdx] = useState(0);
    const [selectedResTypeIdx, setSelectedResTypeIdx] = useState(0);
    const [docsSet, setDocsSet] = useState<string[][]>([]);
    const [availableDocTypes, setAvailableDocTypes] = useState<DocType[]>([]);
    const [availableResults, setAvailableResults] = useState<ResType[]>([]);
    
    useEffect(() => {
        const dTypes = Array.from(new Set([
            ...(phrases?.result.filter(doc => doc.phrase_sets.length > 0).map(doc => doc.doc_type) || []),
            ...(attributes?.result.filter(doc => doc.attributes.length > 0).map(doc => doc.doc_type) || [])
        ]));
        const newDocsSet = dTypes.map(dType => phrases?.result.find(phr => phr.doc_type === dType)!.phrase_sets.reduce((accumulator, currentValue) => { accumulator.push(...currentValue.common_for); return accumulator}, [] as string[]) ?? []);
        setAvailableDocTypes(prevVal => 
            prevVal.length === dTypes.length && 
            prevVal.every((val, idx) => val === dTypes[idx])? prevVal : dTypes
        );
        setDocsSet(prevVal =>
            prevVal.length === newDocsSet.length &&
            prevVal.every((val, idx) => val === newDocsSet[idx])? prevVal : newDocsSet
        );
    }, [phrases, attributes]);
    
    useEffect(() => {
        setSelectedDocTypeIdx(prevVal =>
            availableDocTypes.length === 0? 0 : 
                prevVal >= availableDocTypes.length? availableDocTypes.length-1 :
                    prevVal
        );
    }, [availableDocTypes]);
    
    useEffect(() => {
        if (selectedDocTypeIdx < availableDocTypes.length){
            const results: ResType[] = [
                (phrases !== undefined && phrases.result.some(phr =>
                    phr.doc_type === availableDocTypes[selectedDocTypeIdx] && phr.phrase_sets.length > 0)? "Phrases" : undefined),
                (attributes !== undefined && attributes.result.some(attr =>
                    attr.doc_type === availableDocTypes[selectedDocTypeIdx] && attr.attributes.length > 0)? "Attributes" : undefined)
            ].filter(res => res !== undefined) as ResType[];

            setAvailableResults(prevVal =>
                prevVal.length === results.length &&
                prevVal.every((val, idx) => val === results[idx])? 
                    prevVal : results
            );
        }
    }, [phrases, attributes, availableDocTypes, selectedDocTypeIdx]);

    useEffect(() => {
        setSelectedResTypeIdx(prevVal =>
            availableResults.length === 0? 0 :
                prevVal >= availableResults.length? availableResults.length-1 :
                    prevVal
        );
    }, [availableResults]);
    
    return (
        <>
            {availableDocTypes.length === 0? 
                <p className="info-text">For some reason, there is no results available. Does this category contain any documents?</p> : 
                <>
                    {availableDocTypes.length > 0? <SelectOptionsPanel options={availableDocTypes} selectedIdx={selectedDocTypeIdx} setSelectedIdx={setSelectedDocTypeIdx}/> : <></>}
                    {availableResults.length > 1? <SelectOptionsPanel options={availableResults} selectedIdx={selectedResTypeIdx} setSelectedIdx={setSelectedResTypeIdx}/> : <></>}
                    {availableResults[selectedResTypeIdx] === "Phrases"? 
                        <DocumentPhrasesView docsSet={docsSet[selectedDocTypeIdx]} phrases={phrases?.result.find(phr => phr.doc_type === availableDocTypes[selectedDocTypeIdx])?.phrase_sets ?? []}/> :
                        <DocumentAttributesView docsSet={docsSet[selectedDocTypeIdx]} attributes={attributes?.result.find(phr => phr.doc_type === availableDocTypes[selectedDocTypeIdx])?.attributes ?? []}/> }
                    {availableDocTypes.length === 0 || availableResults[selectedResTypeIdx] === "Attributes"? <></> : <TestCustomPhraseComponent cat_id={cat_id} selectedDocType={availableDocTypes[selectedDocTypeIdx]}/>}
                </>
            }
        </>
    );
};

export default memo(ResultsViewComponent);
